import React, { useMemo, useState } from "react";
import type {
  HessProductKpi,
  HessProductMonthlySeries,
  HessReportResponse,
} from "../../types";
import { MetricCard } from "../../components/MetricCard";
import { BarChart } from "../../components/BarChart";
import { CARD_BG, SHADOW, TEXT, BORDER } from "../../config/theme";

interface HessLayoutProps {
  data: HessReportResponse;
  reportId?: string;
}

const formatMoney = (value: string) =>
  Number(value).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

const formatMoney2 = (value: string) =>
  Number(value).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatPct = (ratio: string | null) => {
  if (!ratio) return "—";
  const value = Number(ratio);
  if (!Number.isFinite(value)) return "—";
  return `${(value * 100).toFixed(1)}%`;
};

const shortMonth = (isoDate: string) => {
  const dt = new Date(`${isoDate}T00:00:00`);
  return dt.toLocaleString("en-US", { month: "short" });
};

const sumNumbers = (values: number[]) => values.reduce((acc, v) => acc + v, 0);

const PRODUCT_COLORS: Record<string, string> = {
  "Allomi": "#22C55E", // green
  "MVRK": "#A855F7", // purple
  "HS Cab": "#F59E0B", // amber
};

const productColor = (name: string) => PRODUCT_COLORS[name] ?? "#2563EB";

const formatUnitsLabel = (value: number) => Math.round(value).toLocaleString();

// Bar labels for comparison charts: compact thousands with 1 decimal, no "k" suffix
const formatUnitsComparisonDataLabel = (value: number) => {
  const abs = Math.abs(value);

  // Values < 1000 show as integers
  if (abs < 1000) return Math.round(value).toLocaleString("en-US");

  // Keep 1 decimal for thousands (e.g. 1.2, 9.8)
  if (abs < 10000) {
    const k = abs / 1000;
    return `${value < 0 ? "-" : ""}${k.toFixed(1)}`;
  }

  // Fall back to integer thousands for very large numbers
  const kInt = Math.round(abs / 1000);
  return `${value < 0 ? "-" : ""}${kInt}`;
};

const pctChange = (current: number, yago: number): number | null => {
  if (!Number.isFinite(current) || !Number.isFinite(yago) || yago === 0)
    return null;
  return ((current - yago) / yago) * 100;
};

const formatPct1 = (value: number) => `${value.toFixed(1)}%`;

const darkenColor = (hex: string, percent: number) => {
  const num = parseInt(hex.replace("#", ""), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) - amt;
  const G = ((num >> 8) & 0x00ff) - amt;
  const B = (num & 0x0000ff) - amt;
  return `#${(
    0x1000000 +
    (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
    (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
    (B < 255 ? (B < 1 ? 0 : B) : 255)
  )
    .toString(16)
    .slice(1)}`;
};

const sortBy = <T,>(items: T[], selector: (item: T) => number) =>
  [...items].sort((a, b) => selector(b) - selector(a));

export const HessLayout: React.FC<HessLayoutProps> = ({ data }) => {
  const summary = data.summary;
  const products = data.products;
  const [showMetricCallouts, setShowMetricCallouts] = useState(true);

  const yoyTrend: "up" | "down" | "neutral" =
    summary.yoy_unit_change > 0
      ? "up"
      : summary.yoy_unit_change < 0
        ? "down"
        : "neutral";

  // Calculate days in period for daily averages
  const daysInPeriod = useMemo(() => {
    const start = new Date(`${summary.date_range_start}T00:00:00`);
    const end = new Date(`${summary.date_range_end}T00:00:00`);
    const diffMs = end.getTime() - start.getTime();
    return Math.max(1, Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1);
  }, [summary.date_range_start, summary.date_range_end]);

  const avgDailySales = Number(summary.total_sales) / daysInPeriod;
  const avgDailyUnits = summary.total_units / daysInPeriod;
  const avgDailySpend = Number(summary.total_ad_spend) / daysInPeriod;
  const avgDailyAttributedSales =
    Number(summary.total_attributed_sales) / daysInPeriod;
  const avgDailyAttributedUnits = summary.total_attributed_units / daysInPeriod;

  const monthLabels = useMemo(
    () => data.monthly_totals.map((m) => shortMonth(m.month_start)),
    [data.monthly_totals],
  );

  const monthlyUnits = useMemo(
    () => data.monthly_totals.map((m) => m.total_units),
    [data.monthly_totals],
  );

  const monthlyUnitsYago = useMemo(
    () => data.monthly_totals.map((m) => m.total_units_yago),
    [data.monthly_totals],
  );

  const overallMonthlyCallout = useMemo(() => {
    const currentTotal = sumNumbers(monthlyUnits);
    const yagoTotal = sumNumbers(monthlyUnitsYago);
    return `Period total: ${currentTotal.toLocaleString()} units (YAGO ${yagoTotal.toLocaleString()})`;
  }, [monthlyUnits, monthlyUnitsYago]);

  const costPerUnit = (p: HessProductKpi) =>
    p.cost_per_total_unit ? Number(p.cost_per_total_unit) : Number.NaN;

  const ntbPct = (p: HessProductKpi) =>
    (p.ntb_sales_pct ? Number(p.ntb_sales_pct) : 0) * 100;

  const yoyUnits = (p: HessProductKpi) => Number(p.yoy_unit_change);

  const yoyUnitsPct = (p: HessProductKpi) =>
    p.yoy_unit_change_pct ? Number(p.yoy_unit_change_pct) * 100 : 0;

  const costByProduct = useMemo(
    () => sortBy(products, costPerUnit),
    [products],
  );
  const ntbByProduct = useMemo(() => sortBy(products, ntbPct), [products]);
  const yoyByProduct = useMemo(() => sortBy(products, yoyUnits), [products]);
  const yoyPctByProduct = useMemo(
    () => sortBy(products, yoyUnitsPct),
    [products],
  );

  const costCallout = useMemo(() => {
    const withCost = products
      .filter((p) => p.cost_per_total_unit)
      .filter((p) => Number(p.cost_per_total_unit) > 0);
    if (withCost.length === 0) return "Lowest cost/unit: —";

    const best = withCost.reduce((acc, p) =>
      costPerUnit(p) < costPerUnit(acc) ? p : acc,
    );

    return `Lowest cost/unit: ${formatMoney2(best.cost_per_total_unit!)} — ${best.product_name}`;
  }, [products]);

  const ntbCallout = useMemo(() => {
    const withPct = products.filter((p) => p.ntb_sales_pct);
    if (withPct.length === 0) return "Highest NTB%: —";

    const best = withPct.reduce((acc, p) =>
      Number(p.ntb_sales_pct) > Number(acc.ntb_sales_pct) ? p : acc,
    );

    return `Highest NTB%: ${formatPct(best.ntb_sales_pct)} — ${best.product_name}`;
  }, [products]);

  const yoyCallout = useMemo(() => {
    if (products.length === 0) return "Largest YoY change: —";

    const bestUp = products.reduce((acc, p) =>
      p.yoy_unit_change > acc.yoy_unit_change ? p : acc,
    );
    const bestDown = products.reduce((acc, p) =>
      p.yoy_unit_change < acc.yoy_unit_change ? p : acc,
    );

    if (bestUp.yoy_unit_change > 0) {
      return `Largest increase: +${bestUp.yoy_unit_change.toLocaleString()} units — ${bestUp.product_name}`;
    }
    if (bestDown.yoy_unit_change < 0) {
      return `Largest decrease: ${bestDown.yoy_unit_change.toLocaleString()} units — ${bestDown.product_name}`;
    }

    return "No YoY unit change across products";
  }, [products]);

  const yoyPctCallout = useMemo(() => {
    const withPct = products.filter((p) => p.yoy_unit_change_pct);
    if (withPct.length === 0) return "YoY Unit Change %: —";

    const bestUp = withPct.reduce((acc, p) =>
      yoyUnitsPct(p) > yoyUnitsPct(acc) ? p : acc,
    );
    const bestDown = withPct.reduce((acc, p) =>
      yoyUnitsPct(p) < yoyUnitsPct(acc) ? p : acc,
    );

    const bestUpVal = yoyUnitsPct(bestUp);
    const bestDownVal = yoyUnitsPct(bestDown);

    if (bestUpVal > 0) {
      return `Largest increase: +${bestUpVal.toFixed(1)}% — ${bestUp.product_name}`;
    }
    if (bestDownVal < 0) {
      return `Largest decrease: ${bestDownVal.toFixed(1)}% — ${bestDown.product_name}`;
    }

    return "No YoY unit change across products";
  }, [products]);

  const productMonthlyTotals = useMemo(() => {
    const desiredOrder = ["Allomi", "MVRK", "HS Cab"];
    const orderIndex = new Map(desiredOrder.map((name, idx) => [name, idx]));

    const series = data.product_monthly_totals ?? [];
    return [...series].sort((a, b) => {
      const aIdx = orderIndex.get(a.product_name) ?? 999;
      const bIdx = orderIndex.get(b.product_name) ?? 999;
      return aIdx - bIdx;
    });
  }, [data.product_monthly_totals]);

  const productMonthlyCallout = (series: HessProductMonthlySeries) => {
    const currentTotal = sumNumbers(
      series.monthly_totals.map((m) => m.total_units),
    );
    const yagoTotal = sumNumbers(
      series.monthly_totals.map((m) => m.total_units_yago),
    );
    return `Period total: ${currentTotal.toLocaleString()} units (YAGO ${yagoTotal.toLocaleString()})`;
  };
  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className={`text-3xl font-bold ${TEXT.primary}`}>
            Hess Dashboard
          </h1>
          <p className={`mt-1 text-sm ${TEXT.secondary}`}>
            Window: {summary.date_range_start} to {summary.date_range_end}
          </p>
        </div>

        <button
          type="button"
          role="switch"
          aria-checked={showMetricCallouts}
          aria-label="Metric callouts"
          onClick={() => setShowMetricCallouts((current) => !current)}
          className={`inline-flex items-center justify-between gap-3 rounded-full border ${BORDER} ${CARD_BG} px-4 py-2 text-sm font-medium ${TEXT.primary} shadow-sm transition-colors`}
        >
          <span>Metric callouts</span>
          <span
            className={`inline-flex h-6 w-11 items-center rounded-full px-1 transition-colors ${
              showMetricCallouts
                ? "bg-blue-600 dark:bg-blue-500"
                : "bg-gray-300 dark:bg-[#6F6263]"
            }`}
            aria-hidden="true"
          >
            <span
              className={`h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                showMetricCallouts ? "translate-x-5" : "translate-x-0"
              }`}
            />
          </span>
        </button>
      </div>

      <div className="mb-8 space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          title="Total Sales"
          value={formatMoney(summary.total_sales)}
          subtitle={`Avg. Daily: ${formatMoney(avgDailySales.toFixed(2))}`}
        />
        <MetricCard
          title="Total Units"
          value={summary.total_units.toLocaleString()}
          subtitle={`Avg. Daily: ${Math.round(avgDailyUnits).toLocaleString()}`}
        />
        <MetricCard
          title="Ad Spend"
          value={formatMoney(summary.total_ad_spend)}
          subtitle={`Avg. Daily: ${formatMoney(avgDailySpend.toFixed(2))}`}
        />
        <MetricCard
          title="YoY Unit Change"
          value={summary.yoy_unit_change.toLocaleString()}
          subtitle={`${summary.total_units_yago.toLocaleString()} units YAGO`}
          trend={yoyTrend}
        />
        </div>

        <div
          className={`${CARD_BG} ${SHADOW.cardHover} border ${BORDER} rounded-2xl p-5`}
        >
          <div className="flex flex-col gap-1 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className={`text-sm font-semibold uppercase tracking-[0.22em] ${TEXT.secondary}`}>
                Attribution Snapshot
              </h2>
              <p className={`mt-1 text-sm ${TEXT.secondary}`}>
                Ad-attributed revenue and unit volume for the selected Hess window.
              </p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <MetricCard
              title="Attributed Sales"
              value={formatMoney(summary.total_attributed_sales)}
              subtitle={`Avg. Daily: ${formatMoney(avgDailyAttributedSales.toFixed(2))}`}
            />
            <MetricCard
              title="Attributed Units"
              value={summary.total_attributed_units.toLocaleString()}
              subtitle={`Avg. Daily: ${Math.round(avgDailyAttributedUnits).toLocaleString()}`}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div
          className={`${CARD_BG} ${SHADOW.cardHover} border ${BORDER} rounded-lg p-6`}
        >
          <h2 className={`text-lg font-semibold ${TEXT.primary}`}>
            Monthly Total Units (Current vs YAGO)
          </h2>
          <p className={`mt-1 mb-4 text-xs ${TEXT.secondary}`}>
            {overallMonthlyCallout}
          </p>
          <BarChart
            labels={monthLabels}
            datasets={[
              {
                type: "bar",
                order: 2,
                yAxisID: "y",
                label: "Total Units",
                data: monthlyUnits,
                backgroundColor: "#3B82F6",
                valueFormatter: formatUnitsLabel,
                dataLabelFormatter: formatUnitsComparisonDataLabel,
              },
              {
                type: "bar",
                order: 3,
                yAxisID: "y",
                label: "Units YAGO",
                data: monthlyUnitsYago,
                backgroundColor: "#9CA3AF",
                valueFormatter: formatUnitsLabel,
                dataLabelFormatter: formatUnitsComparisonDataLabel,
              },
              {
                type: "line",
                order: 1,
                yAxisID: "y1",
                label: "% Change vs YAGO",
                data: monthlyUnits.map(
                  (u, idx) => pctChange(u, monthlyUnitsYago[idx]) ?? null,
                ),
                borderColor: darkenColor("#3B82F6", 30),
                pointBackgroundColor: darkenColor("#3B82F6", 30),
                borderWidth: 2,
                tension: 0.35,
                datalabels: {
                  display: true,
                  backgroundColor: "#F5F5F4", // off-white
                  borderRadius: 4,
                  padding: 4,
                  // All Products: Jul & Aug = bottom, Nov = top but closer
                  align: (context: any) => {
                    const monthIndex = context.dataIndex;
                    if (monthIndex === 0 || monthIndex === 1) return "bottom"; // Jul, Aug
                    return "top";
                  },
                  offset: (context: any) => {
                    const monthIndex = context.dataIndex;
                    if (monthIndex === 1) return 35; // Aug - increased distance
                    if (monthIndex === 4) return 4; // Nov - closer to dot
                    return 8; // default
                  },
                },
                dataLabelFormatter: formatPct1,
              },
            ]}
            stacked={false}
            showLegend={true}
            showDataLabels={showMetricCallouts}
            height="h-72"
          />
        </div>

        {productMonthlyTotals.map((series) => (
          <div
            key={series.upc}
            className={`${CARD_BG} ${SHADOW.cardHover} border ${BORDER} rounded-lg p-6`}
          >
            <h2 className={`text-lg font-semibold ${TEXT.primary}`}>
              Monthly Units — {series.product_name} (Current vs YAGO)
            </h2>
            <p className={`mt-1 mb-4 text-xs ${TEXT.secondary}`}>
              {productMonthlyCallout(series)}
            </p>
            <BarChart
              labels={series.monthly_totals.map((m) =>
                shortMonth(m.month_start),
              )}
              datasets={[
                {
                  type: "bar",
                  order: 2,
                  yAxisID: "y",
                  label: "Total Units",
                  data: series.monthly_totals.map((m) => m.total_units),
                  backgroundColor: productColor(series.product_name),
                  valueFormatter: formatUnitsLabel,
                  dataLabelFormatter: formatUnitsComparisonDataLabel,
                },
                {
                  type: "bar",
                  order: 3,
                  yAxisID: "y",
                  label: "Units YAGO",
                  data: series.monthly_totals.map((m) => m.total_units_yago),
                  backgroundColor: "#9CA3AF",
                  valueFormatter: formatUnitsLabel,
                  dataLabelFormatter: formatUnitsComparisonDataLabel,
                },
                {
                  type: "line",
                  order: 1,
                  yAxisID: "y1",
                  label: "% Change vs YAGO",
                  data: series.monthly_totals.map(
                    (m) => pctChange(m.total_units, m.total_units_yago) ?? null,
                  ),
                  borderColor: darkenColor(
                    productColor(series.product_name),
                    20,
                  ),
                  pointBackgroundColor: darkenColor(
                    productColor(series.product_name),
                    20,
                  ),
                  borderWidth: 2,
                  tension: 0.35,
                  datalabels: {
                    display: true,
                    backgroundColor: "#F5F5F4", // off-white
                    borderRadius: 4,
                    padding: 4,
                    // Allomi: all top, default offset
                    // MVRK: Jul = bottom
                    // HS Cab: Jul = bottom
                    align: (context: any) => {
                      const monthIndex = context.dataIndex;
                      if (series.product_name === "MVRK" && monthIndex === 0)
                        return "bottom"; // Jul
                      if (series.product_name === "HS Cab" && monthIndex === 0)
                        return "bottom"; // Jul
                      return "top";
                    },
                    offset: 8, // default offset for all product charts
                  },
                  dataLabelFormatter: formatPct1,
                },
              ]}
              stacked={false}
              showLegend={true}
              showDataLabels={showMetricCallouts}
              height="h-72"
            />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div
          className={`${CARD_BG} ${SHADOW.cardHover} border ${BORDER} rounded-lg p-6`}
        >
          <h2 className={`text-lg font-semibold ${TEXT.primary}`}>
            Cost per Total Units Sold (Spend / Total Units)
          </h2>
          <p className={`mt-1 mb-4 text-xs ${TEXT.secondary}`}>{costCallout}</p>
          <BarChart
            labels={costByProduct.map((p) => p.product_name)}
            datasets={[
              {
                label: "Cost / Unit",
                data: costByProduct.map(costPerUnit),
                backgroundColor: costByProduct.map((p) =>
                  productColor(p.product_name),
                ),
              },
            ]}
            horizontal
            showLegend={false}
            showDataLabels={true}
            height="h-72"
            valueFormatter={(value) =>
              `$${Number(value).toLocaleString("en-US", { maximumFractionDigits: 2 })}`
            }
          />
        </div>

        <div
          className={`${CARD_BG} ${SHADOW.cardHover} border ${BORDER} rounded-lg p-6`}
        >
          <h2 className={`text-lg font-semibold ${TEXT.primary}`}>
            New-to-Brand % of Attributed Sales
          </h2>
          <p className={`mt-1 mb-4 text-xs ${TEXT.secondary}`}>{ntbCallout}</p>
          <BarChart
            labels={ntbByProduct.map((p) => p.product_name)}
            datasets={[
              {
                label: "NTB %",
                data: ntbByProduct.map(ntbPct),
                backgroundColor: ntbByProduct.map((p) =>
                  productColor(p.product_name),
                ),
              },
            ]}
            horizontal
            showLegend={false}
            showDataLabels={true}
            height="h-72"
            valueFormatter={(value) => `${value.toFixed(1)}%`}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div
          className={`${CARD_BG} ${SHADOW.cardHover} border ${BORDER} rounded-lg p-6`}
        >
          <h2 className={`text-lg font-semibold ${TEXT.primary}`}>
            YoY Unit Change by Product
          </h2>
          <p className={`mt-1 mb-4 text-xs ${TEXT.secondary}`}>{yoyCallout}</p>
          <BarChart
            labels={yoyByProduct.map((p) => p.product_name)}
            datasets={[
              {
                label: "YoY Unit Change",
                data: yoyByProduct.map(yoyUnits),
                backgroundColor: yoyByProduct.map((p) =>
                  productColor(p.product_name),
                ),
              },
            ]}
            horizontal
            showLegend={false}
            showDataLabels={showMetricCallouts}
            height="h-72"
            valueFormatter={formatUnitsLabel}
          />
        </div>

        <div
          className={`${CARD_BG} ${SHADOW.cardHover} border ${BORDER} rounded-lg p-6`}
        >
          <h2 className={`text-lg font-semibold ${TEXT.primary}`}>
            YoY Unit Change % by Product
          </h2>
          <p className={`mt-1 mb-4 text-xs ${TEXT.secondary}`}>
            {yoyPctCallout}
          </p>
          <BarChart
            labels={yoyPctByProduct.map((p) => p.product_name)}
            datasets={[
              {
                label: "YoY Unit Change %",
                data: yoyPctByProduct.map(yoyUnitsPct),
                backgroundColor: yoyPctByProduct.map((p) =>
                  productColor(p.product_name),
                ),
              },
            ]}
            horizontal
            showLegend={false}
            showDataLabels={showMetricCallouts}
            height="h-72"
            valueFormatter={(value) => `${Number(value).toFixed(1)}%`}
          />
        </div>
      </div>

      <div
        className={`${CARD_BG} ${SHADOW.cardHover} border ${BORDER} rounded-lg p-6`}
      >
        <h2 className={`text-lg font-semibold mb-4 ${TEXT.primary}`}>
          Product KPIs
        </h2>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left">
              <tr className="border-b border-gray-200 dark:border-[#5B5353]">
                <th className="py-2 pr-4">Product</th>
                <th className="py-2 pr-4">UPC</th>
                <th className="py-2 pr-4">Total Units</th>
                <th className="py-2 pr-4">YoY Units Δ</th>
                <th className="py-2 pr-4">Cost / Total Unit</th>
                <th className="py-2 pr-4">NTB % (Attrib)</th>
                <th className="py-2 pr-0">Ad Spend</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr
                  key={p.upc}
                  className="border-b border-gray-100 dark:border-[#5B5353]/60"
                >
                  <td className={`py-2 pr-4 ${TEXT.primary}`}>
                    {p.product_name}
                  </td>
                  <td
                    className={`py-2 pr-4 font-mono text-xs ${TEXT.secondary}`}
                  >
                    {p.upc}
                  </td>
                  <td className={`py-2 pr-4 ${TEXT.primary}`}>
                    {p.total_units.toLocaleString()}
                  </td>
                  <td
                    className={`py-2 pr-4 ${
                      p.yoy_unit_change > 0
                        ? "text-green-700 dark:text-green-300"
                        : p.yoy_unit_change < 0
                          ? "text-red-700 dark:text-red-300"
                          : TEXT.secondary
                    }`}
                  >
                    {p.yoy_unit_change.toLocaleString()}
                  </td>
                  <td className={`py-2 pr-4 ${TEXT.primary}`}>
                    {p.cost_per_total_unit
                      ? formatMoney2(p.cost_per_total_unit)
                      : "—"}
                  </td>
                  <td className={`py-2 pr-4 ${TEXT.primary}`}>
                    {formatPct(p.ntb_sales_pct)}
                  </td>
                  <td className={`py-2 pr-0 ${TEXT.primary}`}>
                    {formatMoney(p.ad_spend)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {data.repair_notices && data.repair_notices.length > 0 && (
          <div className="mt-4 text-xs text-gray-500 dark:text-[#B5A9AA]">
            {data.repair_notices.map((notice, idx) => (
              <div key={idx}>{notice.message}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
